import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { api } from './api';
import { date, Empty, Tag, type Props } from './ui';

const FIELDS = [
  { name: 'name', label: '称呼' },
  { name: 'phone', label: '手机号' },
  { name: 'plates', label: '车牌', helper: '多个车牌用逗号或顿号分隔，常用的放前面', wide: true },
  { name: 'note', label: '备注', helper: '例如所属物流公司', wide: true },
];

const body = (d: any) => ({
  name: d.name,
  phone: d.phone ?? '',
  plates: d.plates.join('，'),
  note: d.note,
});

/** Adds a driver (no argument) or edits one; the list reloads through `p.version`. */
export const editDriver = (p: Props, driver?: any) =>
  p.open({
    title: driver ? `编辑司机 · ${driver.name || driver.phone}` : '新增司机',
    description: driver
      ? '修改不影响已有的提货记录。'
      : '提前录入常来的师傅，他第一次扫码授权手机号后就能直接认出来。',
    fields: FIELDS,
    form: driver ? body(driver) : { name: '', phone: '', plates: '', note: '' },
    submit: (f: any) => api(driver ? `/drivers/${driver.id}` : '/drivers', f),
  });

/**
 * Drivers who collect goods. The list fills itself as drivers confirm pickups by scanning the
 * delivery note; regular drivers can be entered ahead so their first scan already knows them.
 * Adding one is the page heading's action.
 */
export default function DriverPanel(p: Props) {
  const [drivers, setDrivers] = useState<any[] | null>(null);
  useEffect(() => {
    api<any[]>('/drivers')
      .then(setDrivers)
      .catch((e) => p.setError(e.message));
  }, [p.version, p.workspace]);

  const edit = (driver?: any) => editDriver(p, driver);
  const toggle = (driver: any) =>
    p.safe(() => api(`/drivers/${driver.id}`, { ...body(driver), active: !driver.active }));

  return (
    <Paper>
      {drivers?.length ? (
        <Table
          sx={{ '& th, & td': { px: 1.5 }, '& th:first-of-type, & td:first-of-type': { pl: 3 } }}
        >
          <TableHead>
            <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableCell>司机</TableCell>
              <TableCell>常用车牌</TableCell>
              <TableCell align="right">提货</TableCell>
              <TableCell align="right">最近提货</TableCell>
              <TableCell align="right" sx={{ pr: 3 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {drivers.map((d) => (
              <TableRow key={d.id} hover sx={{ opacity: d.active ? 1 : 0.55 }}>
                <TableCell sx={{ width: '32%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {d.name || '未留称呼'}
                    </Typography>
                    {!d.active && <Tag tone="neutral">已停用</Tag>}
                  </Box>
                  <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.25 }}>
                    {d.phone
                      ? `${d.phone}${d.phone_verified ? ' · 微信已验证' : ''}`
                      : '未留手机号'}
                    {d.note && ` · ${d.note}`}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>{d.plates.join('、') || '—'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                  {d.pickups ? `${d.pickups} 次` : '—'}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                  {d.last_pickup_at ? date(d.last_pickup_at) : '—'}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', pr: 3 }}>
                  <Button size="small" onClick={() => edit(d)}>
                    编辑
                  </Button>
                  <Button size="small" color="inherit" onClick={() => toggle(d)}>
                    {d.active ? '停用' : '启用'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Empty>
          {drivers ? '还没有司机。司机第一次扫码确认提货后会自动出现在这里。' : '正在加载…'}
        </Empty>
      )}
    </Paper>
  );
}
